/**
 * A location login is a counter, not a person, and the routes treat it so.
 *
 * `/l/<code>` used to sit behind the same "already signed in? go home" gate as
 * `/login`, and home is CenoX Home — the platform, with AI and every other
 * product on it. A counter's session outlives the tab, so the second time
 * anybody opened their location's link they were signed in, and every link —
 * Fire Service, Holy Ghost, External — opened the platform instead of the
 * location. These gates send a location session to its own location instead,
 * from the link and from every platform-level page it could otherwise reach.
 *
 * Only routing. What a location account can read or write is decided by the
 * API and by row level security on its bound location; this keeps it off
 * screens that are not for it, it does not make anything safe.
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { MembershipSummary } from '@cenox/shared';
import { useAuth } from './auth';
import { Spinner } from '../components/ui';

/**
 * Where a location session belongs: `/business`, which resolves it to its one
 * location's workspace (see `useWorkerHome`).
 */
export const LOCATION_HOME = '/business';

/** Is this session a location login? `some`, so any doubt resolves toward the narrower view. */
export function isLocationSession(memberships: readonly MembershipSummary[]): boolean {
  return memberships.some((m) => m.role === 'location_operator');
}

/**
 * The gate in front of `/l/<code>`.
 *
 *   signed out          → the location's sign-in screen
 *   a location session  → straight to its location
 *   a person's account  → the location's sign-in screen, so an owner checking
 *                         the link sees what the counter will see
 */
export function LocationLinkEntry({ children }: { children: ReactNode }): ReactNode {
  const { user, memberships, loading } = useAuth();

  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner />
      </div>
    );
  }
  if (user && isLocationSession(memberships)) return <Navigate to={LOCATION_HOME} replace />;
  return <>{children}</>;
}

/**
 * Platform pages — CenoX Home, AI, the other products, the account itself.
 * None of them is a location's business, so a location session is sent back
 * to its location. Mount inside RequireAuth, which has already waited for the
 * session.
 */
export function NotForLocationSessions({ children }: { children: ReactNode }): ReactNode {
  const { memberships } = useAuth();
  if (isLocationSession(memberships)) return <Navigate to={LOCATION_HOME} replace />;
  return <>{children}</>;
}
